import * as THREE from 'three';
import { SNOW_LINE, SNOW_REGIONS } from './weather.js';
import { FLAG_WATER, TERRAIN, type GameMap } from '@sunderfen/shared';
import { stylize } from './stylize.js';
import { activeLook } from './look.js';

const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);
export function hash2(x: number, z: number) {
  let h = (Math.imul(x | 0, 374761393) + Math.imul(z | 0, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
const mix = (a: number[], b: number[], t: number) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

/** Colour of each tile (sRGB 0..1), shared by the 3D terrain and the 2D maps. */
export function tileColors(map: GameMap): number[][] {
  const out: number[][] = new Array(map.width * map.height);
  const rock = map.terrains.find(t => t.id === TERRAIN.rock)?.color ?? [0.5, 0.49, 0.47];
  for (let z = 0; z < map.height; z++) for (let x = 0; x < map.width; x++) {
    const t = map.idx(x, z), ter = map.terrain[t], h = map.heights[t] * map.heightUnit;
    const grass = map.regions.find(r => r.id === map.region[t])?.grass ?? [0.5, 0.66, 0.32];
    // smooth noise across tiles: a per-tile random shade (and the old 4×4 blocks) showed as a grid on flat ground
    const n = patchNoise(x + 0.5, z + 0.5, 3.3) * 0.6 + patchNoise(x - 37.5, z + 11.5, 9) * 0.4;
    let c: number[];
    if (ter === TERRAIN.seabed || (map.flags[t] & FLAG_WATER)) c = mix([0.78, 0.72, 0.52], [0.2, 0.3, 0.32], clamp(-h / 1.4, 0, 1));
    else if (ter === TERRAIN.rock) c = mix(mix(rock, [0.42, 0.42, 0.43], n), grass, 0.12);
    else if (ter === TERRAIN.snow) c = mix([0.88, 0.88, 0.86], rock, n * 0.3);
    else if (ter === TERRAIN.grass) { const k = 0.9 + n * 0.18; c = [grass[0] * k, grass[1] * k, grass[2] * k]; }
    else if (ter === TERRAIN.path) c = mix(grass, [0.68, 0.57, 0.4], 0.78);
    else { const base = map.terrains.find(d => d.id === ter)?.color ?? grass; const k = 0.95 + n * 0.08; c = [base[0] * k, base[1] * k, base[2] * k]; }
    // snow lies on the high ground of the ridges, patchy at the snow line
    if (!(map.flags[t] & FLAG_WATER) && ter !== TERRAIN.snow) {
      const key = map.regions.find(r => r.id === map.region[t])?.key ?? '';
      if (SNOW_REGIONS.includes(key)) {
        const k = clamp((h - SNOW_LINE + 0.4) / 1.0 + (n - 0.5) * 0.5, 0, 1);
        if (k > 0) c = mix(c, [0.9, 0.92, 0.95], k * 0.88);
      }
    }
    out[t] = gradeGround(c, x, z, !!(map.flags[t] & FLAG_WATER) || ter === TERRAIN.seabed);
  }
  return out;
}

/** Smooth value noise on a coarse grid: broad drifts of lusher and drier ground. */
function patchNoise(x: number, z: number, s: number) {
  const fx = x / s, fz = z / s, ix = Math.floor(fx), iz = Math.floor(fz), u = fx - ix, v = fz - iz;
  const su = u * u * (3 - 2 * u), sv = v * v * (3 - 2 * v);
  const a = hash2(ix, iz), b = hash2(ix + 1, iz), c = hash2(ix, iz + 1), d = hash2(ix + 1, iz + 1);
  return a + (b - a) * su + (c - a) * sv + (a - b - c + d) * su * sv;
}
/** The look's ground grade: pull saturation down, darken, give it an earthy cast and break up big flat fields. */
function gradeGround(c: number[], x: number, z: number, wet: boolean): number[] {
  const G = activeLook().grade.ground;
  if (G.sat === 1 && G.value === 1 && G.patch === 0) return c;
  const l = c[0] * 0.2126 + c[1] * 0.7152 + c[2] * 0.0722;
  let o = [l + (c[0] - l) * G.sat, l + (c[1] - l) * G.sat, l + (c[2] - l) * G.sat];
  if (!wet) {
    o = [o[0] * G.cast[0], o[1] * G.cast[1], o[2] * G.cast[2]];
    const p = (patchNoise(x, z, 14) * 0.6 + patchNoise(x + 101, z - 57, 31) * 0.4 - 0.5) * 2;
    // drier patches go yellow-brown, lusher ones darker and greener
    o = p > 0 ? mix(o, [o[0] * 1.1 + 0.03, o[1] * 1.02 + 0.01, o[2] * 0.8], p * G.patch * 2) : mix(o, [o[0] * 0.78, o[1] * 0.88, o[2] * 0.8], -p * G.patch * 2);
  }
  return [o[0] * G.value, o[1] * G.value, o[2] * G.value].map(v => clamp(v, 0, 1));
}

export interface Occluder { x: number; z: number; r: number; s: number }
/** One material for every terrain chunk (one shader program). */
let terrainMat: THREE.Material | undefined;

/**
 * Faceted terrain with colours blended across tile edges and ambient occlusion baked into
 * the vertex colours (creases, cliff feet, the ground under trees and houses, wet shorelines).
 * Built for one rectangle of tiles [x0, x1) × [z0, x1) at a time (a chunk), so a big world only
 * builds what is near the camera; neighbouring chunks meet exactly because every value comes
 * from the whole map. Per frame each chunk is a single plain Lambert mesh.
 */
export function buildTerrain(map: GameMap, colors: number[][], occluders: Occluder[], x0 = 0, z0 = 0, x1 = map.width, z1 = map.height): THREE.Mesh {
  const W = map.width, H = map.height, CW = x1 - x0, CH = z1 - z0, VW = CW + 1;
  const rock = map.terrains.find(t => t.id === TERRAIN.rock)?.color ?? [0.5, 0.49, 0.47];
  // per-vertex colour: average of the tiles touching the vertex (vertex indices are local to the chunk)
  const vc = new Float32Array(VW * (CH + 1) * 3);
  for (let lz = 0; lz <= CH; lz++) for (let lx = 0; lx <= CW; lx++) {
    const x = x0 + lx, z = z0 + lz;
    let r = 0, g = 0, b = 0, n = 0;
    for (let dz = -1; dz <= 0; dz++) for (let dx = -1; dx <= 0; dx++) {
      const X = x + dx, Z = z + dz;
      if (X < 0 || Z < 0 || X >= W || Z >= H) continue;
      const c = colors[X + Z * W]; r += c[0]; g += c[1]; b += c[2]; n++;
    }
    const i = (lx + lz * VW) * 3;
    vc[i] = r / n; vc[i + 1] = g / n; vc[i + 2] = b / n;
  }
  // ambient occlusion
  const occ = new Float32Array(VW * (CH + 1));
  for (let lz = 0; lz <= CH; lz++) for (let lx = 0; lx <= CW; lx++) {
    const x = x0 + lx, z = z0 + lz, h = map.vertexHeight(x, z);
    let s = 0, n = 0;
    for (let dz = -2; dz <= 2; dz++) for (let dx = -2; dx <= 2; dx++) {
      const X = x + dx, Z = z + dz;
      if ((!dx && !dz) || X < 0 || Z < 0 || X > W || Z > H) continue;
      s += map.vertexHeight(X, Z); n++;
    }
    let o = clamp((s / n - h) * 0.3, 0, 0.32);
    if (h > -0.05 && h < 0.35) o += 0.14 * (1 - h / 0.35); // damp ground at the waterline
    occ[lx + lz * VW] = o;
  }
  for (const oc of occluders) {
    const r = Math.ceil(oc.r);
    if (oc.x + r < x0 || oc.x - r > x1 || oc.z + r < z0 || oc.z - r > z1) continue;
    for (let z = Math.floor(oc.z) - r; z <= Math.ceil(oc.z) + r; z++) for (let x = Math.floor(oc.x) - r; x <= Math.ceil(oc.x) + r; x++) {
      if (x < x0 || z < z0 || x > x1 || z > z1) continue;
      const d = Math.hypot(x - oc.x, z - oc.z);
      if (d < oc.r) occ[(x - x0) + (z - z0) * VW] += (1 - d / oc.r) * oc.s;
    }
  }

  const N = CW * CH, pos = new Float32Array(N * 18), col = new Float32Array(N * 18), nor = new Float32Array(N * 18);
  // smooth normals from the whole heightfield (so chunk edges agree), with a little of each
  // face's own tilt left in: the ground reads as rolling land rather than a field of triangles
  const vh = (x: number, z: number) => map.vertexHeight(clamp(x, 0, W), clamp(z, 0, H));
  const smoothN = (x: number, z: number) => { const nx = (vh(x - 1, z) - vh(x + 1, z)) / 2, nz = (vh(x, z - 1) - vh(x, z + 1)) / 2, l = Math.hypot(nx, 1, nz); return [nx / l, 1 / l, nz / l]; };
  let p = 0;
  const lin = (v: number) => Math.pow(clamp(v, 0, 1), 2.2);
  const face = (verts: [number, number][], fx: number, fz: number, salt: number) => {
    const hs = verts.map(([x, z]) => map.vertexHeight(x, z));
    // face normal y, to paint steep faces as rock
    const ax = verts[1][0] - verts[0][0], ay = hs[1] - hs[0], az = verts[1][1] - verts[0][1];
    const bx = verts[2][0] - verts[0][0], by = hs[2] - hs[0], bz = verts[2][1] - verts[0][1];
    const nx = ay * bz - az * by, ny = az * bx - ax * bz, nz = ax * by - ay * bx;
    const nl = Math.hypot(nx, ny, nz), sg = ny < 0 ? -1 : 1, fn = [sg * nx / nl, sg * ny / nl, sg * nz / nl];
    const ny1 = Math.abs(ny) / nl;
    const t = fx + fz * W;
    const steep = map.flags[t] & FLAG_WATER ? 0 : clamp((0.84 - ny1) / 0.22, 0, 1);
    const grass = map.regions.find(r => r.id === map.region[t])?.grass ?? [0.5, 0.6, 0.3];
    const rockC = mix(mix(rock, [0.4, 0.4, 0.41], hash2(fx, fz)), grass, 0.1);
    void salt;
    for (let k = 0; k < 3; k++) {
      const [x, z] = verts[k], vi = (x - x0) + (z - z0) * VW;
      let c = [vc[vi * 3], vc[vi * 3 + 1], vc[vi * 3 + 2]];
      // steepness and the small colour jitter come from the vertex (shared by every face that
      // meets there), so colour blends across the ground instead of changing triangle by triangle
      const sn0 = smoothN(x, z), vSteep = map.flags[t] & FLAG_WATER ? 0 : clamp((0.86 - sn0[1]) / 0.2, 0, 1);
      const jitter = 0.96 + hash2(x * 7 + 3, z * 5 - 1) * 0.08;
      if (vSteep) c = mix(c, rockC, vSteep * 0.85);
      void steep;
      const ao = 1 - clamp(occ[vi], 0, 0.55);
      pos[p] = x; pos[p + 1] = hs[k]; pos[p + 2] = z;
      { const sn = smoothN(x, z), FACET = 0; let a = sn[0] * (1 - FACET) + fn[0] * FACET, b = sn[1] * (1 - FACET) + fn[1] * FACET, cc = sn[2] * (1 - FACET) + fn[2] * FACET; const l = Math.hypot(a, b, cc); nor[p] = a / l; nor[p + 1] = b / l; nor[p + 2] = cc / l; }
      col[p] = lin(c[0] * ao * jitter); col[p + 1] = lin(c[1] * ao * jitter); col[p + 2] = lin(c[2] * ao * jitter);
      p += 3;
    }
  };
  for (let z = z0; z < z1; z++) for (let x = x0; x < x1; x++) {
    // alternate the diagonal so the facets don't line up in stripes
    if ((x + z) & 1) {
      face([[x, z], [x, z + 1], [x + 1, z]], x, z, 1);
      face([[x + 1, z], [x, z + 1], [x + 1, z + 1]], x, z, 2);
    } else {
      face([[x, z], [x, z + 1], [x + 1, z + 1]], x, z, 1);
      face([[x, z], [x + 1, z + 1], [x + 1, z]], x, z, 2);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  geo.computeBoundingSphere();
  terrainMat ??= stylize(new THREE.MeshLambertMaterial({ vertexColors: true }), { rim: false, caustics: true, ground: true });
  const mesh = new THREE.Mesh(geo, terrainMat);
  mesh.receiveShadow = true;
  return mesh;
}
